import React from "react"

import {
  Button,
  ChoiceCard,
  ChoiceCardGroup,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

import { AudioPreviewButton } from "./AudioPreviewButton"

interface OptionCardProps {
  label: string
  description?: string
  badge?: React.ReactNode
  previewUrl?: string
  previewVolume?: number
  isSelected: boolean
  isDisabled?: boolean
  currentlyPlayingAudioId: string | null
  onPlayingChange: (playingId: string | null) => void
  onSelect: () => void
  /** A control shown left of the audio preview, e.g. a Change button. */
  action?: React.ReactNode
  /** "plain" for the current choice on the form; the picker keeps the card. */
  variant?: "card" | "plain"
}

/**
 * A `ChoiceCard` with an audio preview in its trailing slot, so every audio
 * picker on the persona page reads the same way.
 */
export function OptionCard({
  label,
  description,
  badge,
  previewUrl,
  previewVolume,
  isSelected,
  isDisabled = false,
  currentlyPlayingAudioId,
  onPlayingChange,
  onSelect,
  action,
  variant,
}: OptionCardProps) {
  const preview = previewUrl ? (
    <AudioPreviewButton
      previewUrl={previewUrl}
      audioLabel={label}
      volume={previewVolume}
      currentlyPlayingId={currentlyPlayingAudioId}
      onPlayingChange={onPlayingChange}
    />
  ) : undefined

  return (
    <ChoiceCard
      title={label}
      description={description}
      badge={badge}
      selected={isSelected}
      disabled={isDisabled}
      onSelect={onSelect}
      variant={variant}
      // Reserve room for the trailing controls so the text never runs
      // underneath them.
      className={cn("gap-2 p-2", action && (preview ? "pr-32" : "pr-20"))}
      trailing={
        action ? (
          <div className="flex items-center gap-2">
            {action}
            {preview}
          </div>
        ) : (
          preview
        )
      }
    />
  )
}

interface PersonaOptionPickerDialogProps {
  isOpen: boolean
  title: string
  description?: React.ReactNode
  onClose: () => void
  /** Rendered above the option cards, e.g. a warning. */
  banner?: React.ReactNode
  /** Rendered below the option cards, e.g. a volume slider or actions. */
  footer?: React.ReactNode
  children: React.ReactNode
}

/**
 * Shell for a call-environment picker: title, optional note, option cards,
 * and Done. Selection applies as you click, the same as the face customizer.
 */
export function PersonaOptionPickerDialog({
  isOpen,
  title,
  description,
  onClose,
  banner,
  footer,
  children,
}: PersonaOptionPickerDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <p className="text-xs text-coval-text-muted">{description}</p>
          )}
        </DialogHeader>
        <DialogBody className="space-y-3">
          {banner}
          <ChoiceCardGroup
            aria-label={title}
            className="max-h-[50vh] space-y-2 overflow-y-auto pr-1"
          >
            {children}
          </ChoiceCardGroup>
          {footer}
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
